import React from "react";

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Key a phasing entry by its month, in UTC — same convention used when writing it.
const monthKey = (date) => {
  const d = new Date(date);
  return `${d.getUTCFullYear()}-${d.getUTCMonth()}`;
};

// The months covered by [startAt, endAt], as {key, year, monthIndex, iso}. Capped to stay sane
// if someone types a far-future end date.
const monthsBetween = (startAt, endAt) => {
  if (!startAt || !endAt) return [];
  const start = new Date(startAt);
  const end = new Date(endAt);
  if (isNaN(start) || isNaN(end) || end < start) return [];

  const months = [];
  let year = start.getUTCFullYear();
  let monthIndex = start.getUTCMonth();
  while (months.length < 120 && (year < end.getUTCFullYear() || (year === end.getUTCFullYear() && monthIndex <= end.getUTCMonth()))) {
    months.push({ key: `${year}-${monthIndex}`, year, monthIndex, iso: new Date(Date.UTC(year, monthIndex, 1)).toISOString() });
    monthIndex++;
    if (monthIndex > 11) {
      monthIndex = 0;
      year++;
    }
  }
  return months;
};

export default ({ values, setValues, onUpdate }) => {
  // Phasing: the months the budget spans, and what has been typed for each.
  const phasingMonths = monthsBetween(values.startAt, values.endAt);
  const phasedByMonth = {};
  for (const entry of values.monthly || []) phasedByMonth[monthKey(entry.date)] = entry.value;

  // Entries that no longer fall inside [startAt, endAt] — kept and flagged, never dropped silently.
  const orphanPhasing = (values.monthly || []).filter((entry) => !phasingMonths.some((month) => month.key === monthKey(entry.date)));
  const phasedTotal = (values.monthly || []).reduce((sum, entry) => sum + (Number(entry.value) || 0), 0);

  const setPhasing = (month, value) => {
    const rest = (values.monthly || []).filter((entry) => monthKey(entry.date) !== month.key);
    const next = value === "" ? rest : [...rest, { date: month.iso, value: Number(value) }];
    next.sort((a, b) => new Date(a.date) - new Date(b.date));
    setValues({ ...values, monthly: next });
  };

  const spreadEvenly = () => {
    if (!phasingMonths.length) return;
    const perMonth = Math.round(((Number(values.amount) || 0) / phasingMonths.length) * 100) / 100;
    setValues({ ...values, monthly: phasingMonths.map((month) => ({ date: month.iso, value: perMonth })) });
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-6 gap-4">
        <div className="col-span-1 space-y-2">
          <label className="block text-sm font-medium text-gray-700" htmlFor="budget-value">
            Value
          </label>
          <input
            id="budget-value"
            type="number"
            className="input w-full"
            value={values.value}
            onChange={(e) => onUpdate({ value: e.target.value })}
            onWheel={(e) => e.target.blur()}
          />
        </div>
        <div className="col-span-1 space-y-2">
          <label className="block text-sm font-medium text-gray-700" htmlFor="budget-frequency">
            Frequency
          </label>
          <select id="budget-frequency" className="input w-full" value={values.frequency || "monthly"} onChange={(e) => onUpdate({ frequency: e.target.value })}>
            <option value="monthly">Monthly</option>
            <option value="one-time">One-time</option>
          </select>
        </div>
        <div className="col-span-1 space-y-2">
          <label className="block text-sm font-medium text-gray-700" htmlFor="budget-startAt">
            Start Date
          </label>
          <input
            id="budget-startAt"
            className="input w-full"
            type="date"
            value={values.startAt ? values.startAt.split("T")[0] : ""}
            onChange={(e) => onUpdate({ startAt: e.target.value })}
          />
        </div>
        <div className="col-span-1 space-y-2">
          <label className="block text-sm font-medium text-gray-700" htmlFor="budget-duration">
            Duration (months)
          </label>
          <input id="budget-duration" className="input w-full" type="number" min="1" value={values.duration || ""} onChange={(e) => onUpdate({ duration: e.target.value })} />
        </div>
        <div className="col-span-1 space-y-2">
          <label className="block text-sm font-medium text-gray-700" htmlFor="budget-endAt">
            End Date
          </label>
          <input
            id="budget-endAt"
            className="input w-full"
            type="date"
            value={values.endAt ? values.endAt.split("T")[0] : ""}
            onChange={(e) => onUpdate({ endAt: e.target.value })}
          />
        </div>
        <div className="col-span-1 space-y-2">
          <label className="block text-sm font-medium text-gray-700" htmlFor="budget-amount">
            Amount*
          </label>
          <div className="relative">
            <input
              disabled
              id="budget-amount"
              className="input w-full pl-6"
              type="number"
              value={values.amount}
              onChange={(e) => onUpdate({ amount: e.target.value })}
              placeholder="0.00"
            />
            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500">€</span>
          </div>
        </div>
      </div>

      {/* Billing Type Section */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Billing Type</label>
        <div className="grid grid-cols-2 gap-4">
          {[
            {
              type: "regie",
              label: "Régie",
              description: "Billing based on actual time spent and resources used",
            },
            {
              type: "forfait",
              label: "Forfait",
              description: "Fixed-price billing with predefined scope and deliverables",
            },
          ].map((option) => (
            <div
              key={option.type}
              className={`p-4 border rounded-lg cursor-pointer transition-all hover:shadow-md ${
                values.facturation_type === option.type ? "border-blue-500 bg-blue-50" : "border-gray-200"
              }`}
              onClick={() => setValues({ ...values, facturation_type: option.type })}>
              <div className="flex items-center gap-2 mb-2">
                <input type="radio" checked={values.facturation_type === option.type} onChange={() => setValues({ ...values, facturation_type: option.type })} />
                <span className="font-medium">{option.label}</span>
              </div>
              <p className="text-sm text-gray-600">{option.description}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="block text-sm font-medium text-gray-700">
            Répartition mensuelle <span className="font-normal text-gray-500">— prévu, pas consommé. Vide = étalement linéaire.</span>
          </label>
          <div className="flex items-center gap-3 text-sm">
            <button type="button" className="text-blue-600 hover:underline disabled:text-gray-300 disabled:no-underline" onClick={spreadEvenly} disabled={!phasingMonths.length}>
              Répartir également
            </button>
            {(values.monthly || []).length > 0 && (
              <button type="button" className="text-gray-500 hover:underline" onClick={() => setValues({ ...values, monthly: [] })}>
                Effacer
              </button>
            )}
          </div>
        </div>

        {!phasingMonths.length ? (
          <p className="text-sm text-gray-500">Renseigne une date de début et une date de fin pour répartir le budget.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {phasingMonths.map((month) => (
              <div key={month.key} className="w-24">
                <span className="block text-xs text-gray-500 mb-1">
                  {MONTH_LABELS[month.monthIndex]} {String(month.year).slice(2)}
                </span>
                <input
                  className="input w-full text-right"
                  type="number"
                  placeholder="—"
                  value={phasedByMonth[month.key] ?? ""}
                  onChange={(e) => setPhasing(month, e.target.value)}
                  onWheel={(e) => e.target.blur()}
                />
              </div>
            ))}
          </div>
        )}

        {(values.monthly || []).length > 0 && (
          <p className={`text-sm ${Math.abs(phasedTotal - (Number(values.amount) || 0)) > 1 ? "text-red-600" : "text-gray-500"}`}>
            Total réparti : {Math.round(phasedTotal).toLocaleString("fr-FR")} € / {Math.round(Number(values.amount) || 0).toLocaleString("fr-FR")} €
          </p>
        )}

        {orphanPhasing.length > 0 && (
          <p className="text-sm text-amber-600">
            {orphanPhasing.length} mois hors période — conservés. Ajuste les dates ou efface la répartition.
          </p>
        )}
      </div>

      <Milestones values={values} setValues={setValues} />
    </div>
  );
};

function Milestones({ values, setValues }) {
  const milestones = values.billing_milestones || [];
  const update = (index, patch) => {
    const next = milestones.map((milestone, i) => (i === index ? { ...milestone, ...patch } : milestone));
    setValues({ ...values, billing_milestones: next });
  };
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">Billing milestones</label>
        <button
          type="button"
          className="text-sm text-blue-600 hover:underline"
          onClick={() => setValues({ ...values, billing_milestones: [...milestones, { due: "", amount: null, percent: null }] })}>
          Add milestone
        </button>
      </div>
      <p className="text-xs text-gray-500">A deposit plus a balance. Due is a date or an event (signature, delivery). Fill an amount or a percent.</p>
      {milestones.map((milestone, index) => (
        <div key={index} className="grid grid-cols-4 gap-2 items-center">
          <input className="input col-span-2" placeholder="2026-11-01 or signature" value={milestone.due || ""} onChange={(e) => update(index, { due: e.target.value })} />
          <input className="input" type="number" placeholder="€" value={milestone.amount ?? ""} onChange={(e) => update(index, { amount: e.target.value === "" ? null : Number(e.target.value) })} />
          <div className="flex gap-2">
            <input className="input" type="number" placeholder="%" value={milestone.percent ?? ""} onChange={(e) => update(index, { percent: e.target.value === "" ? null : Number(e.target.value) })} />
            <button type="button" className="text-sm text-red-600" onClick={() => setValues({ ...values, billing_milestones: milestones.filter((_, i) => i !== index) })}>
              Remove
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
